<template>
  <section v-if="canManageTeam" class="team-section">
    <h3 class="account-section__title">Team</h3>

    <p class="team-section__seats">
      {{ members.length }} of {{ maxSeats }}
      {{ maxSeats === 1 ? "seat" : "seats" }} used
    </p>

    <ul class="team-roster">
      <li v-for="row in members" :key="row.member.id" class="team-roster__row">
        <span class="team-roster__email">{{ emailOf(row) }}</span>
        <span
          v-if="row.role === 'OWNER'"
          class="ds-pill ds-pill--outline team-roster__tag"
        >
          Owner
        </span>
        <button
          v-else
          type="button"
          class="ds-button ds-button--ghost team-roster__remove"
          :disabled="busy"
          @click="remove(row.member.id)"
        >
          Remove
        </button>
      </li>
    </ul>

    <div v-if="hasOpenSeats" class="team-invite">
      <span class="ds-field-label">Invite link</span>
      <div class="team-invite__row">
        <input
          class="team-invite__input"
          :value="inviteLink"
          readonly
          @focus="$event.target.select()"
        />
        <button
          type="button"
          class="ds-button ds-button--primary"
          :disabled="busy || !inviteToken"
          @click="copyLink"
        >
          {{ copied ? "Copied" : "Copy" }}
        </button>
      </div>
      <button
        type="button"
        class="ds-link team-invite__regen"
        :disabled="busy"
        @click="regenerate"
      >
        Regenerate link
      </button>
    </div>
    <p v-else class="team-section__full">
      All {{ maxSeats }} seats are filled. Remove a member to free one up.
    </p>
  </section>
</template>

<script>
import {
  getTeamDetail,
  regenerateInviteToken,
  removeTeamMember,
} from "../auth";

export default {
  name: "TeamSection",
  props: {
    teamId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      busy: false,
      copied: false,
      members: [],
      maxSeats: 0,
      inviteToken: "",
    };
  },
  computed: {
    // Only multi-seat teams have anything to manage.
    canManageTeam() {
      return !!this.teamId && this.maxSeats > 1;
    },
    inviteLink() {
      if (!this.inviteToken) return "";
      return `${window.location.origin}/?invite=${this.inviteToken}`;
    },
    hasOpenSeats() {
      return this.members.length < this.maxSeats;
    },
  },
  async mounted() {
    if (this.teamId) await this.load();
  },
  methods: {
    emailOf(row) {
      return row?.member?.auth?.email ?? "—";
    },
    async load() {
      try {
        const team = await getTeamDetail(this.teamId);
        this.members = team?.members ?? [];
        this.maxSeats = team?.maxTeamMembers ?? this.members.length;
        this.inviteToken = team?.inviteToken ?? "";
      } catch (err) {
        console.error("[team] load failed:", err);
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.inviteLink);
        this.copied = true;
        window.setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error("[team] copy failed:", err);
      }
    },
    async regenerate() {
      this.busy = true;
      try {
        const token = await regenerateInviteToken(this.teamId);
        if (token) this.inviteToken = token;
      } catch (err) {
        console.error("[team] regenerate failed:", err);
      } finally {
        this.busy = false;
      }
    },
    async remove(memberId) {
      this.busy = true;
      try {
        await removeTeamMember({ teamId: this.teamId, memberId });
        await this.load();
      } catch (err) {
        console.error("[team] remove failed:", err);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.team-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.account-section__title {
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mute);
  margin: 0;
}

.team-section__seats {
  font-size: 0.85rem;
  color: var(--mute);
  margin: 0;
}

.team-roster {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.team-roster__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
}

.team-roster__row + .team-roster__row {
  border-top: 1px solid var(--line);
}

.team-roster__email {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink);
  font-size: 0.9rem;
}

.team-roster__tag {
  flex: none;
}

.team-roster__remove {
  flex: none;
}

.team-invite {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.team-invite__row {
  display: flex;
  gap: 8px;
}

.team-invite__input {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface-50);
  color: var(--ink);
  font-size: 0.85rem;
  font-family: var(--font-sans);
}

.team-invite__regen {
  align-self: flex-start;
  font-size: 0.82rem;
}

.team-section__full {
  font-size: 0.85rem;
  color: var(--mute);
  margin: 0;
}
</style>
